// src/lib/api/underlyings.ts

import {
  UnderlyingSearchParams,
  UserSectorConfig,
  SectorAllocation,
  AllocationRecommendation,
  UnderlyingWithPreferences
} from '@/types/underlyings';

import { getApiBaseUrl } from '@/lib/http-utils';
const API_BASE = `${getApiBaseUrl()}/api/underlyings`;

async function buildApiError(response: Response, fallback: string): Promise<Error> {
  let message = `${fallback}: ${response.status}`;

  try {
    const contentType = response.headers.get('content-type') || '';
    if (contentType.includes('application/json')) {
      const data = await response.json();
      const detail = data?.error || data?.message;
      if (detail) {
        message = `${message} ${detail}`;
      }
    } else {
      const text = await response.text();
      if (text) {
        message = `${message} ${text.slice(0, 200)}`;
      }
    }
  } catch {
    // Keep the status-only message.
  }

  return new Error(message);
}

export const underlyingsApi = {
  // Core operations
  async getAll(params?: UnderlyingSearchParams): Promise<UnderlyingWithPreferences[]> {
    const searchParams = new URLSearchParams();
    
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value === undefined) return;
        // `symbols: string[]` is sent as repeated `symbol=` params, which the
        // route reads via searchParams.getAll("symbol").
        if (key === "symbols" && Array.isArray(value)) {
          value.forEach((symbol) => searchParams.append("symbol", String(symbol)));
          return;
        }
        searchParams.append(key, value.toString());
      });
    }

    const query = searchParams.toString();
    const response = await fetch(query ? `${API_BASE}?${query}` : API_BASE);
    if (!response.ok) {
      throw await buildApiError(response, 'Failed to fetch underlyings');
    }
    
    const data = await response.json();
    return data.data;
  },

  async getBySymbol(symbol: string): Promise<UnderlyingWithPreferences | null> {
    const response = await fetch(`${API_BASE}/${encodeURIComponent(symbol)}`);

    if (response.status === 404) {
      return null;
    }

    if (!response.ok) {
      throw new Error(`Failed to fetch underlying: ${response.status}`);
    }

    const data = await response.json();
    return data.data ?? null;
  },

  async search(query: string): Promise<UnderlyingWithPreferences[]> {
    return this.getAll({ query });
  },

  // Sector management
  async getSectors(): Promise<string[]> {
    const response = await fetch(`${API_BASE}/sectors`);
    if (!response.ok) throw new Error('Failed to fetch sectors');
    
    const data = await response.json();
    return data.data;
  },

  async createSector(sectorName: string): Promise<void> {
    const response = await fetch(`${API_BASE}/sectors`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ sectorName })
    });
    
    if (!response.ok) throw new Error('Failed to create sector');
  },

  async updateSymbolSector(symbol: string, sector: string): Promise<void> {
    const response = await fetch(`${API_BASE}/${symbol}/sector`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ sector })
    });
    
    if (!response.ok) throw new Error('Failed to update sector');
  },

  async deleteSymbol(symbol: string): Promise<void> {
    const response = await fetch(`${API_BASE}/${symbol}`, {
      method: 'DELETE'
    });
    
    if (!response.ok) throw new Error('Failed to delete symbol');
  },

  async getSectorConfig(): Promise<UserSectorConfig[]> {
    const response = await fetch(`${API_BASE}/sectors/config`);
    if (!response.ok) throw new Error('Failed to fetch sector config');
    
    const data = await response.json();
    return data.data;
  },

  async updateSectorConfig(configs: UserSectorConfig[]): Promise<void> {
    const response = await fetch(`${API_BASE}/sectors/config`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ configs })
    });
    
    if (!response.ok) throw new Error('Failed to update sector config');
  },

  // User preferences
  async toggleFavorite(symbol: string): Promise<boolean> {
    const response = await fetch(`${API_BASE}/${symbol}/favorite`, {
      method: 'PUT'
    });
    
    if (!response.ok) throw new Error('Failed to toggle favorite');
    
    const data = await response.json();
    return data.data.isFavorite;
  },

  async toggleWatchlist(symbol: string): Promise<boolean> {
    const response = await fetch(`${API_BASE}/${symbol}/watchlist`, {
      method: 'PUT'
    });
    
    if (!response.ok) throw new Error('Failed to toggle watchlist');
    
    const data = await response.json();
    return data.data.isWatchlist;
  },

  async toggleExclude(symbol: string): Promise<boolean> {
    const response = await fetch(`${API_BASE}/${symbol}/exclude`, {
      method: 'PUT'
    });
    
    if (!response.ok) throw new Error('Failed to toggle exclusion');
    
    const data = await response.json();
    return data.data.isExcluded;
  },

  async updateNotes(symbol: string, notes: string): Promise<void> {
    const response = await fetch(`${API_BASE}/${symbol}/notes`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ notes })
    });
    
    if (!response.ok) throw new Error('Failed to update notes');
  },

  // Portfolio integration (placeholder - will be implemented in Phase 4)
  async getAllocationSummary(): Promise<SectorAllocation[]> {
    const response = await fetch(`${API_BASE}/allocation/summary`);
    if (!response.ok) throw new Error('Failed to fetch allocation summary');
    
    const data = await response.json();
    return data.data;
  },

  async getAllocationRecommendations(): Promise<AllocationRecommendation[]> {
    const response = await fetch(`${API_BASE}/allocation/recommendations`);
    if (!response.ok) throw new Error('Failed to fetch allocation recommendations');
    
    const data = await response.json();
    return data.data;
  },

  // Watchlist operations
  async getWatchlists(): Promise<Array<{
    name: string;
    groupName: string;
    count: number;
    symbols: string[];
  }>> {
    const response = await fetch(`${API_BASE}/watchlists`);
    if (!response.ok) throw new Error('Failed to fetch watchlists');
    
    const data = await response.json();
    return data.data;
  },

  // Bulk sector update
  async updateSectorsBulk(symbols: string[]): Promise<{
    success: boolean;
    updated: number;
    failed: number;
    error?: string;
  }> {
    const response = await fetch(`${API_BASE}/update-sectors`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ symbols })
    });
    
    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.error || 'Failed to update sectors');
    }
    
    return data;
  }
};
